import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ClothesIcon } from "./icons/Clothes";
import { PatchIcon } from "./icons/Patch";

/**
 * Collection filter type for Trophy Room items
 */
export type CollectionFilter = "clothes" | "patch" | null;

const switchButtonVariants = cva(
  "flex flex-1 gap-2 items-center justify-center min-h-0 min-w-0 px-2 py-2 transition-all duration-200 cursor-pointer",
  {
    variants: {
      state: {
        active:
          "bg-gray-500 text-white rounded-[0.3125rem] shadow-[1px_1px_2px_-1px_rgba(51,51,51,0.33)]",
        inactive: "bg-gray-50 text-gray-500",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  }
);

export interface CollectionSwitchProps {
  /**
   * Current filter state: "clothes", "patch", or null (empty - no filter)
   */
  value?: CollectionFilter;
  /**
   * Callback when filter changes
   */
  onChange?: (value: CollectionFilter) => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Disable the switch
   */
  disabled?: boolean;
}

/**
 * CollectionSwitch - ANYW3AR-specific toggle switch for filtering Trophy Room items
 *
 * This component allows users to filter between clothes and patches, or show all items (empty state).
 * Clicking an active button deselects it, returning to the empty/no-filter state.
 *
 * States:
 * - "clothes": Only clothes are shown
 * - "patch": Only patches are shown
 * - null (empty): No filter applied, all items shown
 *
 * @example
 * ```tsx
 * const [filter, setFilter] = useState<CollectionFilter>(null)
 *
 * <CollectionSwitch value={filter} onChange={setFilter} />
 * ```
 */
export const CollectionSwitch = React.forwardRef<
  HTMLDivElement,
  CollectionSwitchProps
>(({ value = null, onChange, className, disabled = false }, ref) => {
  const handleClick = (filterType: "clothes" | "patch") => {
    if (disabled) return;

    // Toggle: if already selected, deselect (return to null/empty state)
    const newValue = value === filterType ? null : filterType;
    onChange?.(newValue);
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-auto gap-0",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      role="group"
      aria-label="Collection filter"
    >
      {/* Clothes Button */}
      <button
        type="button"
        onClick={() => handleClick("clothes")}
        disabled={disabled}
        className={cn(
          switchButtonVariants({
            state: value === "clothes" ? "active" : "inactive",
          })
        )}
        aria-pressed={value === "clothes"}
        aria-label="Filter by clothes"
      >
        <ClothesIcon size={12} />
        <span className="font-montserrat font-bold text-[15px] leading-normal uppercase">
          Clothes
        </span>
      </button>

      {/* Patch Button */}
      <button
        type="button"
        onClick={() => handleClick("patch")}
        disabled={disabled}
        className={cn(
          switchButtonVariants({
            state: value === "patch" ? "active" : "inactive",
          })
        )}
        aria-pressed={value === "patch"}
        aria-label="Filter by patches"
      >
        <PatchIcon size={12} />
        <span className="font-montserrat font-bold text-[15px] leading-normal uppercase">
          Patches
        </span>
      </button>
    </div>
  );
});

CollectionSwitch.displayName = "CollectionSwitch";
